/**
 * Back-in-Stock capture form (Phase 1 — capture only)
 * ---------------------------------------------------
 * Isolated fetcher + renderer for the panel-managed `back-in-stock`
 * envelope (/api/back-in-stock-config, schemaVersion 1). Replaces the
 * third-party useAmp "Back in Stock" app with a first-party, panel-managed
 * notify-me form. Phase 1 = CAPTURE ONLY: it renders the form when the
 * selected variant is unavailable and POSTs the subscription to the
 * restock-notifier service. Restock DETECTION + ranked/throttled/quiet-hours
 * SEND is Phase 2 (owned entirely by the backend service — this file never
 * sends anything).
 *
 * Fail-safe by construction (mirrors delivery-estimate.js): any failure path
 * (no config URL global, HTTP error, bad JSON, schema mismatch, master
 * `enabled:false`, no anchors, variant available) renders NOTHING and the
 * page is byte-identical to today. The form appears ONLY when the panel
 * config is enabled AND the selected variant is unavailable.
 *
 * Variant availability is read from the theme's product form:
 *   - the `variant:change` CustomEvent (bubbles to document; detail.variant
 *     carries `.id`, `.available`, `.sku`) fired by both the stock Prestige
 *     variant-picker (theme.js) and the gear-builder picker (agb-variant.js);
 *   - the server-rendered initial state from each `.back-in-stock` anchor's
 *     data-* attributes, cross-checked against the `<buy-buttons>` element
 *     (which gets the `hidden` attribute when sold out) / the gear-builder's
 *     `#add-all-btn-2[data-oos-hidden]`.
 *
 * Config loading mirrors delivery-estimate.js exactly: fetched once per
 * page-load, 5-min TTL + ETag in env-namespaced localStorage keys, endpoint
 * from `window.AGB_BACK_IN_STOCK_CONFIG_URL` (+ `?env=preview` on preview),
 * region declared via the X-Store-Region header from `window.AGB_STORE_REGION`.
 * Subscription POSTs go to `window.AGB_RESTOCK_NOTIFIER_URL` + '/subscribe'.
 * Preview-only diagnostics under the `back_in_stock` console category.
 */

(function () {
    'use strict';

    if (typeof window === 'undefined') return;
    if (window.__AGB_BACK_IN_STOCK_LOADED__) return;
    window.__AGB_BACK_IN_STOCK_LOADED__ = true;

    var LOG_PREFIX = '[back-in-stock]';
    var CONFIG_TTL_MS = 5 * 60 * 1000;

    // Live vs Preview storefront detection (identical to delivery-estimate.js /
    // gear-suggestions-config.js — each engine file is self-contained so it can
    // load in any order; preview is the safe default).
    function _agbDetectStorefrontEnv() {
        try {
            var urlForce = new URLSearchParams(location.search).get('storefront_env');
            if (urlForce === 'preview' || urlForce === 'live') return urlForce;
        } catch (_) {}
        try {
            var stored = localStorage.getItem('AGB_StorefrontEnv');
            if (stored === 'preview' || stored === 'live') return stored;
        } catch (_) {}
        var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
        if (role === 'main') return 'live';
        if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
        var host = (location && location.hostname) || '';
        if (host.indexOf('.shopifypreview.com', host.length - 20) !== -1) return 'preview';
        if (host.indexOf('.myshopify.com', host.length - 14) !== -1) return 'preview';
        if (host === '127.0.0.1' || host === 'localhost') return 'preview';
        try {
            if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
        } catch (_) {}
        return 'preview';
    }

    var BIS_ENV = _agbDetectStorefrontEnv();
    var LS_CONFIG_KEY = 'AGB_BackInStock_' + BIS_ENV;
    var LS_CONFIG_ETAG = 'AGB_BackInStockEtag_' + BIS_ENV;
    var LS_CONFIG_AT = 'AGB_BackInStockFetchedAt_' + BIS_ENV;

    // Diagnostics print on PREVIEW only — never on live. Honors the panel's
    // dev-logs category toggle window.__AGB_consoleCategories.back_in_stock
    // (undefined = on, explicit false = silenced — same convention as
    // delivery_estimate / gear-suggestions / custom-labels).
    function _dbg() {
        try {
            if (BIS_ENV !== 'preview') return;
            var cats = window.__AGB_consoleCategories;
            if (cats && typeof cats === 'object' && cats.back_in_stock === false) return;
            console.log.apply(console, arguments);
        } catch (e) {}
    }

    // ===== Shared state =======================================================

    // The validated + enabled config, or null (invalid / disabled / not-yet
    // fetched). Nothing renders while this is null.
    var _activeConfig = null;
    // Tri-state page availability of the selected variant: null=unknown,
    // true=available (no form), false=unavailable (show form).
    var _currentAvailable = null;
    // The currently-selected variant, read at submit time (avoids stale
    // closures when the shopper switches variants with a form open).
    var _currentVariant = { id: '', sku: '' };

    // ===== Language / helpers =================================================

    function docLang() {
        try {
            return ((document.documentElement && document.documentElement.lang) || 'en')
                .split('-')[0].toLowerCase() || 'en';
        } catch (e) { return 'en'; }
    }

    function getVid() {
        try { return localStorage.getItem('visitorID') || null; } catch (e) { return null; }
    }

    function tzHint() {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
    }

    function isValidEmail(v) {
        return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    // Localized string picker: config.text[lang][key] -> text.en[key] -> the
    // built-in default (so a missing/partial config can never break the form).
    var DEFAULT_TEXT = {
        heading: 'Email when available',
        emailPlaceholder: 'Email address',
        consentLabel: 'Notify me when this product is available & add me to mailing list',
        submitButton: 'Notify me',
        successMessage: "We'll email you when this product is back in stock.",
        errorMessage: 'Something went wrong — please try again.',
        privacyNote: ''
    };

    function makeTextPicker(cfg) {
        var lang = docLang();
        var t = (cfg && cfg.text && typeof cfg.text === 'object') ? cfg.text : {};
        var en = (t.en && typeof t.en === 'object') ? t.en : {};
        var loc = (t[lang] && typeof t[lang] === 'object') ? t[lang] : {};
        return function (key) {
            if (typeof loc[key] === 'string' && loc[key]) return loc[key];
            if (typeof en[key] === 'string' && en[key]) return en[key];
            return DEFAULT_TEXT[key] != null ? DEFAULT_TEXT[key] : '';
        };
    }

    // ===== Availability detection =============================================

    /**
     * Initial availability of the selected variant, read from the SAME
     * server-rendered signals the theme exposes:
     *   1. any `.back-in-stock` anchor's data-variant-available ("true"/"false");
     *   2. the `<buy-buttons>` element (gets `hidden` when sold out);
     *   3. the gear-builder's `#add-all-btn-2` (gets `data-oos-hidden`).
     * Returns true/false, or null when nothing conclusive is present (then no
     * form renders — we never guess "sold out").
     */
    function readInitialAvailability() {
        try {
            var a = document.querySelector('.back-in-stock[data-variant-available]');
            if (a) {
                var v = (a.getAttribute('data-variant-available') || '').trim().toLowerCase();
                if (v === 'true') return true;
                if (v === 'false') return false;
            }
        } catch (e) {}
        try {
            var bb = document.querySelector('buy-buttons');
            if (bb) return !bb.hasAttribute('hidden');
        } catch (e) {}
        try {
            var atc = document.querySelector('#add-all-btn-2');
            if (atc) return !atc.hasAttribute('data-oos-hidden');
        } catch (e) {}
        return null;
    }

    function readInitialVariant() {
        try {
            var a = document.querySelector('.back-in-stock[data-variant-id]');
            if (a) {
                return {
                    id: (a.getAttribute('data-variant-id') || '').trim(),
                    sku: (a.getAttribute('data-variant-sku') || '').trim()
                };
            }
        } catch (e) {}
        return { id: '', sku: '' };
    }

    // ===== Styles =============================================================

    var STYLE_ID = 'agb-back-in-stock-style';

    function ensureStyles() {
        if (document.getElementById(STYLE_ID)) return;
        var style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = [
            '.agb-bis{margin-top:.75rem;padding:.85rem .95rem;border:1px solid rgba(0,0,0,.12);',
            'border-radius:2px;font-size:.8125rem;line-height:1.45;color:inherit;}',
            '.agb-bis__heading{display:block;font-weight:600;text-transform:uppercase;',
            'letter-spacing:.05em;font-size:.6875rem;opacity:.7;margin-bottom:.55rem;}',
            '.agb-bis__row{display:flex;gap:.5rem;flex-wrap:wrap;}',
            '.agb-bis__email{flex:1 1 12rem;min-width:0;padding:.55rem .65rem;font-size:.875rem;',
            'color:inherit;background:transparent;border:1px solid rgba(0,0,0,.25);border-radius:2px;}',
            '.agb-bis__email:focus{outline:none;border-color:currentColor;}',
            '.agb-bis__submit{flex:0 0 auto;padding:.55rem 1.1rem;font-size:.75rem;font-weight:600;',
            'text-transform:uppercase;letter-spacing:.06em;cursor:pointer;color:#fff;background:#8f1814;',
            'border:1px solid #8f1814;border-radius:2px;}',
            '.agb-bis__submit:hover{opacity:.9;}',
            '.agb-bis__submit[disabled]{opacity:.5;cursor:default;}',
            '.agb-bis__consent{display:flex;align-items:flex-start;gap:.45rem;margin-top:.55rem;',
            'font-size:.75rem;line-height:1.4;opacity:.85;cursor:pointer;}',
            '.agb-bis__consent input{flex:none;margin-top:.15em;}',
            '.agb-bis__privacy{margin-top:.4rem;font-size:.6875rem;opacity:.6;}',
            '.agb-bis__msg{margin-top:.5rem;font-size:.8125rem;min-height:1em;}',
            '.agb-bis__msg--ok{color:#2e7d32;}',
            '.agb-bis__msg--err{color:#8f1814;}',
            '.agb-bis__done{display:flex;align-items:center;gap:.7rem;padding:.5rem .15rem;}',
            '.agb-bis__done-icon{flex:none;width:1.75rem;height:1.75rem;border-radius:50%;',
            'background:#2e7d32;color:#fff;display:flex;align-items:center;justify-content:center;',
            'font-size:1rem;line-height:1;font-weight:700;}',
            '.agb-bis__done-text{font-size:.875rem;font-weight:600;line-height:1.45;}',
            '.agb-bis[dir="rtl"] .agb-bis__consent{flex-direction:row;text-align:right;}',
            '.agb-bis[dir="rtl"] .agb-bis__done{flex-direction:row;text-align:right;}'
        ].join('');
        document.head.appendChild(style);
    }

    // ===== Render =============================================================

    function clearAnchor(anchor) {
        try {
            while (anchor.firstChild) anchor.removeChild(anchor.firstChild);
            anchor.removeAttribute('data-agb-bis-variant');
        } catch (e) {}
    }

    function showMsg(msgEl, text, kind) {
        if (!msgEl) return;
        msgEl.textContent = text || '';
        msgEl.className = 'agb-bis__msg' + (kind ? ' agb-bis__msg--' + kind : '');
    }

    /**
     * POST the subscription to the restock-notifier. Fire-and-forget: on a
     * 2xx call ok(), on anything else (or no URL) call err(). Never throws.
     */
    function postSubscribe(payload, ok, err) {
        var base = window.AGB_RESTOCK_NOTIFIER_URL;
        if (!base || typeof base !== 'string') { err(); return; }
        try {
            var url = base.replace(/\/+$/, '') + '/subscribe';
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true
            }).then(function (res) {
                if (res && res.ok) ok(); else err();
            }).catch(function () { err(); });
        } catch (e) { err(); }
    }

    /**
     * Build the notify-me form into one anchor. Idempotent per variant: if the
     * anchor already holds a form for the current variant, it is left untouched
     * (so a shown success/error message survives spurious re-renders).
     */
    function renderInto(anchor, cfg) {
        try {
            var variantId = _currentVariant.id || '';
            if (anchor.getAttribute('data-agb-bis-variant') === variantId && anchor.firstChild) {
                return; // already rendered for this variant — preserve state
            }
            while (anchor.firstChild) anchor.removeChild(anchor.firstChild);

            var text = makeTextPicker(cfg);
            var isRtl = docLang() === 'he';

            var wrap = document.createElement('div');
            wrap.className = 'agb-bis';
            if (isRtl) wrap.setAttribute('dir', 'rtl');
            // EU store (agiliteinternational) renders tighter under the sold-out
            // bar than ROW/IL — give it extra top spacing there only. Region-
            // conditional so the shared asset stays identical across all 3 stores.
            try { if ((window.AGB_STORE_REGION || '') === 'International-EU') wrap.style.marginTop = '1.75rem'; } catch (e) {}

            var heading = document.createElement('span');
            heading.className = 'agb-bis__heading';
            heading.textContent = text('heading');
            wrap.appendChild(heading);

            var form = document.createElement('form');
            form.className = 'agb-bis__form';
            form.setAttribute('novalidate', 'novalidate');

            var row = document.createElement('div');
            row.className = 'agb-bis__row';

            var email = document.createElement('input');
            email.className = 'agb-bis__email';
            email.type = 'email';
            email.autocomplete = 'email';
            email.setAttribute('inputmode', 'email');
            email.placeholder = text('emailPlaceholder');
            row.appendChild(email);

            var submit = document.createElement('button');
            submit.className = 'agb-bis__submit';
            submit.type = 'submit';
            submit.textContent = text('submitButton');
            row.appendChild(submit);
            form.appendChild(row);

            var consentLabel = document.createElement('label');
            consentLabel.className = 'agb-bis__consent';
            var consentCb = document.createElement('input');
            consentCb.type = 'checkbox';
            // Fail-safe pre-tick: only checked when the panel config explicitly
            // sets consentPrechecked === true. Missing/false field => unchecked
            // (compliant default; EU/IL keep this off, US/ROW may turn it on).
            // The submit handler reads consentCb.checked live, so a pre-ticked
            // box submits consent:true unless the shopper unticks it.
            consentCb.checked = (cfg && cfg.consentPrechecked === true);
            consentLabel.appendChild(consentCb);
            var consentSpan = document.createElement('span');
            consentSpan.textContent = text('consentLabel');
            consentLabel.appendChild(consentSpan);
            form.appendChild(consentLabel);

            var privacy = text('privacyNote');
            if (privacy) {
                var privacyEl = document.createElement('div');
                privacyEl.className = 'agb-bis__privacy';
                privacyEl.textContent = privacy;
                form.appendChild(privacyEl);
            }

            var msg = document.createElement('div');
            msg.className = 'agb-bis__msg';
            msg.setAttribute('role', 'status');
            msg.setAttribute('aria-live', 'polite');
            form.appendChild(msg);

            form.addEventListener('submit', function (e) {
                e.preventDefault();
                var value = (email.value || '').trim();
                if (!isValidEmail(value)) {
                    showMsg(msg, text('errorMessage'), 'err');
                    try { email.focus(); } catch (_) {}
                    return;
                }
                submit.disabled = true;
                showMsg(msg, '', null);

                var payload = {
                    email: value,
                    vid: getVid(),
                    variant_id: String(_currentVariant.id || ''),
                    sku: _currentVariant.sku || '',
                    product_handle: anchor.getAttribute('data-product-handle') || '',
                    product_title: anchor.getAttribute('data-product-title') || '',
                    product_url: anchor.getAttribute('data-product-url') || '',
                    region: window.AGB_STORE_REGION || 'US-ROW',
                    locale: docLang(),
                    consent: !!consentCb.checked,
                    tz_hint: tzHint()
                };

                postSubscribe(payload, function () {
                    // Swap the whole form for a clean confirmation card — the
                    // shopper is done; no leftover disabled inputs.
                    try {
                        while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
                        var done = document.createElement('div');
                        done.className = 'agb-bis__done';
                        done.setAttribute('role', 'status');
                        done.setAttribute('aria-live', 'polite');
                        var doneIcon = document.createElement('span');
                        doneIcon.className = 'agb-bis__done-icon';
                        doneIcon.textContent = '✓';
                        doneIcon.setAttribute('aria-hidden', 'true');
                        var doneText = document.createElement('span');
                        doneText.className = 'agb-bis__done-text';
                        doneText.textContent = text('successMessage');
                        done.appendChild(doneIcon);
                        done.appendChild(doneText);
                        wrap.appendChild(done);
                    } catch (_) {
                        // fail-safe: fall back to the old inline message
                        showMsg(msg, text('successMessage'), 'ok');
                        try {
                            email.disabled = true;
                            consentCb.disabled = true;
                            submit.disabled = true;
                        } catch (_2) {}
                    }
                    _dbg(LOG_PREFIX, 'subscribed', {
                        variant_id: payload.variant_id, consent: payload.consent, region: payload.region
                    });
                }, function () {
                    submit.disabled = false;
                    showMsg(msg, text('errorMessage'), 'err');
                });
            });

            wrap.appendChild(form);
            anchor.appendChild(wrap);
            anchor.setAttribute('data-agb-bis-variant', variantId);
        } catch (e) { /* fail-safe: leave the anchor empty */ }
    }

    /**
     * Reconcile every `.back-in-stock` anchor against the current config +
     * availability: render the form when (enabled config) AND (selected variant
     * unavailable); otherwise clear the anchor.
     */
    function evaluate() {
        var anchors = document.querySelectorAll('.back-in-stock');
        if (!anchors.length) return; // not a PDP (or anchors not shipped yet)

        if (_currentAvailable === null) _currentAvailable = readInitialAvailability();

        var showForm = !!_activeConfig && _currentAvailable === false;
        if (showForm) ensureStyles();
        for (var i = 0; i < anchors.length; i++) {
            if (showForm) renderInto(anchors[i], _activeConfig);
            else clearAnchor(anchors[i]);
        }
    }

    function onVariantChange(e) {
        try {
            var v = e && e.detail && e.detail.variant;
            if (!v) return;
            if (typeof v.available !== 'undefined') _currentAvailable = !!v.available;
            _currentVariant = {
                id: (v.id != null) ? String(v.id) : '',
                sku: (typeof v.sku === 'string') ? v.sku : ''
            };
            evaluate();
        } catch (_) {}
    }

    // ===== Config loading (delivery-estimate.js pattern) ======================

    /**
     * Adopt a config document: keep it only when schemaVersion 1 AND the master
     * `enabled` flag is on, else drop to null (a disabled/invalid config must
     * clear any form rendered from a stale cache). Re-evaluates in place.
     */
    function applyConfig(cfg) {
        try {
            if (!cfg || typeof cfg !== 'object' || cfg.schemaVersion !== 1 || cfg.enabled !== true) {
                _activeConfig = null;
            } else {
                _activeConfig = cfg;
            }
            evaluate();
        } catch (e) { _activeConfig = null; }
    }

    function loadCachedConfig() {
        try {
            var raw = localStorage.getItem(LS_CONFIG_KEY);
            if (!raw) return;
            var parsed = JSON.parse(raw);
            if (parsed && parsed.schemaVersion === 1) applyConfig(parsed);
        } catch (e) { /* ignore */ }
    }

    function fetchConfigFromAPI() {
        var base = window.AGB_BACK_IN_STOCK_CONFIG_URL;
        if (!base) {
            _dbg(LOG_PREFIX, 'no AGB_BACK_IN_STOCK_CONFIG_URL set -- skipping fetch');
            return;
        }
        var endpoint = BIS_ENV === 'preview'
            ? base + (base.indexOf('?') === -1 ? '?' : '&') + 'env=preview'
            : base;
        try {
            var lastFetched = parseInt(localStorage.getItem(LS_CONFIG_AT) || '0', 10);
            if (Date.now() - lastFetched < CONFIG_TTL_MS) return;

            var etag = localStorage.getItem(LS_CONFIG_ETAG);
            var headers = { 'Accept': 'application/json' };
            if (etag) headers['If-None-Match'] = etag;
            try {
                var rgn = window.AGB_STORE_REGION || '';
                if (rgn) headers['X-Store-Region'] = rgn;
            } catch (e) {}

            fetch(endpoint, { headers: headers })
                .then(function (res) {
                    try { localStorage.setItem(LS_CONFIG_AT, String(Date.now())); } catch (e) {}
                    if (res.status === 304) return null; // cached copy already applied at boot
                    if (!res.ok) return null;
                    return res.json().then(function (body) {
                        if (!body || !body.config) return null;
                        var newCfg = body.config;
                        if (newCfg.schemaVersion !== 1) return null;
                        // Persist whatever valid document the panel served —
                        // including a master-disabled one, so the NEXT page load
                        // correctly renders nothing. applyConfig still refuses to
                        // render a disabled one.
                        try { localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(newCfg)); } catch (e) {}
                        try {
                            var newEtag = res.headers.get('ETag');
                            if (newEtag) localStorage.setItem(LS_CONFIG_ETAG, newEtag);
                        } catch (e) {}
                        applyConfig(newCfg); // fresh 200 re-reconciles over the cached paint
                        return null;
                    });
                })
                .catch(function () { /* network failure: keep cached state */ });
        } catch (e) { /* fail-safe */ }
    }

    // ===== Init ===============================================================

    function init() {
        // Seed the current variant + availability from the server-rendered
        // anchors before any event fires.
        _currentVariant = readInitialVariant();
        _currentAvailable = readInitialAvailability();

        // React to variant switches (bubbles to document from the product form).
        document.addEventListener('variant:change', onVariantChange);

        loadCachedConfig();
        // Async refresh; a fresh 200 re-reconciles the anchors in place.
        fetchConfigFromAPI();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
